// src/components/customers/CustomersTab.jsx
// 고객/CRM 탭: 상태 기준 분류 (방문예정 / 투숙중 / 방문완료)

import React, { useState, useMemo, useRef, useCallback } from 'react';
import * as XLSX from 'xlsx';
import { useApp } from '../../context/AppContext';
import { buildMessage, sendMessage, DEFAULT_TEMPLATE_STRINGS, renderTemplate } from '../../services/messageTemplates';
import { fsCreateReviewToken, fsCreateWelcomeToken } from '../../services/firebase';

const REVIEW_BASE_URL = 'https://staymate.co.kr/review';
const WELCOME_BASE_URL = 'https://staymate.co.kr/welcome';

const INTEREST_LABELS = {
  bbq:    { label: '바비큐',     emoji: '🔥' },
  pool:   { label: '수영장',     emoji: '🌊' },
  quiet:  { label: '조용한 객실', emoji: '🛏️' },
  family: { label: '가족 여행',  emoji: '👨‍👩‍👧' },
  group:  { label: '단체 모임',  emoji: '🎉' },
  etc:    { label: '기타',       emoji: '✨' },
};

// ─── 날짜 포맷 ───
function formatDate(dateStr) {
  if (!dateStr) return '-';
  const d = new Date(dateStr + 'T00:00:00');
  return `${d.getFullYear()}.${String(d.getMonth() + 1).padStart(2, '0')}.${String(d.getDate()).padStart(2, '0')}`;
}

function getDaysColor(days) {
  if (days >= 120) return 'var(--red)';
  if (days >= 60) return 'var(--amber)';
  return 'var(--text-secondary)';
}

// ─── 상태 라벨 정의 ───
const STATUS_CONFIG = {
  upcoming:  { label: '방문 예정', bg: '#eff6ff', color: '#1d4ed8', border: '#bfdbfe' },
  staying:   { label: '투숙 중',   bg: '#f0fdf4', color: '#15803d', border: '#bbf7d0' },
  completed: { label: '방문 완료', bg: 'var(--border-light)', color: 'var(--text-muted)', border: 'var(--border)' },
  none:      { label: '신규',      bg: 'var(--border-light)', color: 'var(--text-muted)', border: 'var(--border)' },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.none;
  return (
    <span style={{
      fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 6,
      background: cfg.bg, color: cfg.color, border: `1px solid ${cfg.border}`,
      flexShrink: 0,
    }}>
      {cfg.label}
    </span>
  );
}

function ChannelBadge({ channel }) {
  const map = {
    '야놀자':     { bg: '#fff0f6', color: '#e91e8c' },
    '여기어때':   { bg: '#fff3e0', color: '#e65100' },
    '네이버예약': { bg: '#e8f5e9', color: '#1b5e20' },
    '에어비앤비': { bg: '#fce4ec', color: '#c62828' },
    '부킹닷컴':   { bg: '#e3f2fd', color: '#0d47a1' },
  };
  const style = map[channel] || { bg: 'var(--border-light)', color: 'var(--text-secondary)' };
  if (!channel) return null;
  return (
    <span style={{
      background: style.bg, color: style.color,
      fontSize: 10, fontWeight: 600, padding: '2px 6px', borderRadius: 4,
    }}>
      {channel}
    </span>
  );
}

// ─── 상태별 날짜 문구 ───
function StatusDateLine({ customer }) {
  const { status, statusCheckin, statusCheckout, daysUntil, visits, lastVisit } = customer;

  if (status === 'staying') {
    return (
      <div style={{ fontSize: 12, marginTop: 2, color: '#15803d', fontWeight: 600 }}>
        현재 투숙 중
        <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
          {formatDate(statusCheckin)} 체크인 · {formatDate(statusCheckout)} 체크아웃
        </span>
      </div>
    );
  }

  if (status === 'upcoming') {
    const dayLabel = daysUntil === 1 ? '내일' : daysUntil === 0 ? '오늘' : `${daysUntil}일 후`;
    return (
      <div style={{ fontSize: 12, marginTop: 2, color: '#1d4ed8', fontWeight: 600 }}>
        {dayLabel} 방문 예정
        <span style={{ fontWeight: 400, color: 'var(--text-muted)', marginLeft: 6 }}>
          ({formatDate(statusCheckin)} 체크인)
        </span>
      </div>
    );
  }

  // completed / none
  if (!lastVisit || visits === 0) {
    return (
      <div style={{ fontSize: 12, marginTop: 2, color: 'var(--text-muted)' }}>
        방문 이력 없음
      </div>
    );
  }

  const daysSince = Math.floor((Date.now() - new Date(lastVisit + 'T00:00:00').getTime()) / 86400000);
  return (
    <div style={{ fontSize: 12, marginTop: 2 }}>
      <span style={{ color: 'var(--text-muted)' }}>마지막 방문: </span>
      <span style={{ color: getDaysColor(daysSince), fontWeight: daysSince >= 60 ? 700 : 500 }}>
        {daysSince === 0 ? '오늘' : `${daysSince}일 전`}
      </span>
      <span style={{ color: 'var(--text-muted)', marginLeft: 4 }}>({formatDate(lastVisit)})</span>
      {visits > 1 && (
        <span style={{ color: 'var(--text-muted)', marginLeft: 6 }}>· {visits}회</span>
      )}
    </div>
  );
}

// ─── 고객 상세 패널 ───
function CustomerDetail({ customer, onClose, onSendMessage }) {
  const { getDaysSinceVisit, toggleMarketingConsent, updateLastDiscount, logMessage, showToast, getCustomersWithStatus, state, markReviewRead } = useApp();
  const customerReview = (state.reviews || []).find(r => r.customerId === customer.id || r.customerPhone === customer.phone);
  const [crmSending, setCrmSending] = useState(false);

  const enhanced = getCustomersWithStatus().find(c => c.id === customer.id) || customer;
  const daysSince = getDaysSinceVisit(enhanced.lastVisit);

  async function handleCrmSend() {
    // 방어 로직: 방문 완료 고객이 아니면 차단 (버튼 우회 대비)
    if (enhanced.status === 'upcoming' || enhanced.status === 'staying') {
      showToast('방문 완료 후 재방문 홍보가 가능합니다', 'error'); return;
    }
    if (!enhanced.lastVisit || enhanced.visits === 0) {
      showToast('방문 이력이 없는 고객입니다', 'error'); return;
    }
    if (!enhanced.marketing_consent) {
      showToast('마케팅 수신 동의가 필요합니다', 'error'); return;
    }
    if (enhanced.opt_out) {
      showToast('수신 거부 고객입니다', 'error'); return;
    }
    if (enhanced.lastDiscount) {
      const daysSinceDiscount = Math.floor(
        (Date.now() - new Date(enhanced.lastDiscount).getTime()) / 86400000
      );
      if (daysSinceDiscount < 30) {
        showToast(`${30 - daysSinceDiscount}일 후 재발송 가능합니다`, 'error'); return;
      }
    }

    setCrmSending(true);
    try {
      const msgText = buildMessage('crm', null, { name: enhanced.name });
      await sendMessage({ phone: enhanced.phone, templateId: 'crm', room: null, data: { name: enhanced.name } });
      logMessage({
        customerId: enhanced.id, customerName: enhanced.name, phone: enhanced.phone,
        templateId: 'crm', templateName: 'CRM 메시지', roomId: null, messageText: msgText,
      });
      updateLastDiscount(enhanced.id);
      showToast(`${enhanced.name}님께 CRM 메시지 발송 완료! ✓`);
    } catch {
      showToast('발송 중 오류가 발생했습니다', 'error');
    } finally {
      setCrmSending(false);
    }
  }

  const lastDiscountDays = enhanced.lastDiscount
    ? Math.floor((Date.now() - new Date(enhanced.lastDiscount).getTime()) / 86400000)
    : null;
  // 방문 완료가 아닌 고객(방문 예정·투숙 중·이력 없음)은 재방문 홍보 불가
  const notCompleted = enhanced.status === 'upcoming' || enhanced.status === 'staying'
    || !enhanced.lastVisit || enhanced.visits === 0;
  const crmBlocked = notCompleted || !enhanced.marketing_consent || enhanced.opt_out;
  const crmCooldown = !notCompleted && lastDiscountDays !== null && lastDiscountDays < 30;
  const statusCfg = STATUS_CONFIG[enhanced.status] || STATUS_CONFIG.none;

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 200, background: 'rgba(0,0,0,0.4)' }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute', top: '8%', left: '50%', transform: 'translateX(-50%)',
          background: 'white', borderRadius: 20, padding: '20px 16px 28px',
          maxHeight: '82dvh', overflowY: 'auto',
          maxWidth: 480, width: 'calc(100% - 32px)',
        }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ width: 36, height: 4, borderRadius: 2, background: 'var(--border)', margin: '0 auto 20px' }} />

        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 16 }}>
          <div style={{
            width: 52, height: 52, borderRadius: '50%',
            background: statusCfg.bg, border: `2px solid ${statusCfg.border}`,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 22, fontWeight: 700, color: statusCfg.color, flexShrink: 0,
          }}>
            {enhanced.name?.[0] ?? '?'}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 5, flexWrap: 'wrap' }}>
              <span style={{ fontWeight: 700, fontSize: 18 }}>{enhanced.name}</span>
              <StatusBadge status={enhanced.status} />
              {enhanced.badges?.includes('단골') && (
                <span className="badge badge-green" style={{ fontSize: 10 }}>단골</span>
              )}
              {enhanced.badges?.includes('단체') && (
                <span className="badge badge-gray" style={{ fontSize: 10 }}>단체</span>
              )}
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{enhanced.phone}</div>
          </div>
          <ChannelBadge channel={enhanced.channel} />
        </div>

        {/* 상태 정보 박스 */}
        <div style={{
          background: statusCfg.bg, border: `1.5px solid ${statusCfg.border}`,
          borderRadius: 10, padding: '10px 14px', marginBottom: 14,
        }}>
          <StatusDateLine customer={enhanced} />
        </div>

        {/* 방문 통계 */}
        <div className="stats-grid" style={{ marginBottom: 16 }}>
          <div className="stat-card" style={{ padding: '10px 8px' }}>
            <div className="stat-value" style={{ fontSize: 18 }}>{enhanced.visits}</div>
            <div className="stat-label">완료 방문</div>
          </div>
          <div className="stat-card" style={{ padding: '10px 8px' }}>
            <div className="stat-value" style={{ fontSize: 18, color: getDaysColor(daysSince) }}>
              {enhanced.visits > 0 ? `${daysSince}일` : '-'}
            </div>
            <div className="stat-label">마지막 방문</div>
          </div>
          <div className="stat-card" style={{ padding: '10px 8px' }}>
            <div className="stat-value" style={{ fontSize: 18 }}>{enhanced.guests}명</div>
            <div className="stat-label">최근 인원</div>
          </div>
        </div>

        {/* 상세 정보 */}
        <div className="card" style={{ marginBottom: 12 }}>
          <InfoRow label="최근 객실" value={enhanced.roomName || '-'} />
          <InfoRow label="첫 방문" value={formatDate(enhanced.firstVisit)} />
          <InfoRow
            label="마지막 방문(완료)"
            value={enhanced.visits > 0 ? formatDate(enhanced.lastVisit) : '없음'}
            valueColor={enhanced.visits === 0 ? 'var(--text-muted)' : undefined}
          />
          {lastDiscountDays !== null && (
            <InfoRow
              label="마지막 CRM 발송"
              value={`${lastDiscountDays}일 전`}
              valueColor={crmCooldown ? 'var(--amber)' : 'var(--text-secondary)'}
            />
          )}
        </div>

        {/* 이용 항목 */}
        {(enhanced.last_used_bbq || enhanced.last_used_pool || enhanced.last_used_firewood || enhanced.last_early_checkin) && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>
              이용 내역
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {enhanced.last_used_bbq && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  background: '#fff7ed', border: '1px solid #fed7aa',
                  borderRadius: 10, padding: '8px 12px', minWidth: 70,
                }}>
                  <span style={{ fontSize: 18 }}>🔥</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#c2410c', marginTop: 2 }}>바베큐</span>
                  {enhanced.bbq_count > 1 && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{enhanced.bbq_count}회</span>
                  )}
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(enhanced.last_used_bbq)}</span>
                </div>
              )}
              {enhanced.last_used_pool && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  background: '#eff6ff', border: '1px solid #bfdbfe',
                  borderRadius: 10, padding: '8px 12px', minWidth: 70,
                }}>
                  <span style={{ fontSize: 18 }}>🌊</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#1d4ed8', marginTop: 2 }}>온수풀</span>
                  {enhanced.pool_count > 1 && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{enhanced.pool_count}회</span>
                  )}
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(enhanced.last_used_pool)}</span>
                </div>
              )}
              {enhanced.last_used_firewood && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  background: '#fafaf9', border: '1px solid #d6d3d1',
                  borderRadius: 10, padding: '8px 12px', minWidth: 70,
                }}>
                  <span style={{ fontSize: 18 }}>🪵</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#57534e', marginTop: 2 }}>장작</span>
                  {enhanced.firewood_count > 1 && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{enhanced.firewood_count}회</span>
                  )}
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(enhanced.last_used_firewood)}</span>
                </div>
              )}
              {enhanced.last_early_checkin && (
                <div style={{
                  display: 'flex', flexDirection: 'column', alignItems: 'center',
                  background: '#f0fdf4', border: '1px solid #bbf7d0',
                  borderRadius: 10, padding: '8px 12px', minWidth: 70,
                }}>
                  <span style={{ fontSize: 18 }}>⏰</span>
                  <span style={{ fontSize: 11, fontWeight: 700, color: '#15803d', marginTop: 2 }}>얼리체크인</span>
                  {enhanced.early_checkin_count > 1 && (
                    <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{enhanced.early_checkin_count}회</span>
                  )}
                  <span style={{ fontSize: 10, color: 'var(--text-muted)' }}>{formatDate(enhanced.last_early_checkin)}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* 취향 태그 */}
        {enhanced.interests?.length > 0 && (
          <div className="card" style={{ marginBottom: 12 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', marginBottom: 8 }}>
              관심사
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
              {enhanced.interests.map(id => {
                const info = INTEREST_LABELS[id];
                if (!info) return null;
                return (
                  <span key={id} style={{
                    fontSize: 12, fontWeight: 600, padding: '5px 10px', borderRadius: 20,
                    background: '#f0fdf4', border: '1px solid #bbf7d0', color: '#15803d',
                  }}>
                    {info.emoji} {info.label}
                  </span>
                );
              })}
            </div>
          </div>
        )}

        {/* 리뷰 */}
        {customerReview && (
          <div
            className="card"
            style={{ marginBottom: 12, background: customerReview.read ? 'white' : '#fffbeb', border: customerReview.read ? undefined : '1.5px solid #f59e0b' }}
            onClick={() => { if (!customerReview.read) markReviewRead(customerReview.id); }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <div style={{ fontWeight: 700, fontSize: 14 }}>
                ⭐ 리뷰
                {!customerReview.read && (
                  <span style={{ marginLeft: 8, fontSize: 10, padding: '2px 7px', borderRadius: 10, background: '#f59e0b', color: 'white', fontWeight: 700 }}>NEW</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: 2 }}>
                {[1,2,3,4,5].map(n => (
                  <span key={n} style={{ fontSize: 16, filter: n <= customerReview.rating ? 'none' : 'grayscale(1) opacity(0.3)' }}>⭐</span>
                ))}
              </div>
            </div>
            {customerReview.comment && (
              <div style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: customerReview.privateFeedback ? 8 : 0, lineHeight: 1.5 }}>
                "{customerReview.comment}"
              </div>
            )}
            {customerReview.privateFeedback && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', background: '#f9fafb', borderRadius: 8, padding: '8px 10px', lineHeight: 1.5 }}>
                <span style={{ fontWeight: 700, color: 'var(--text-muted)' }}>🔒 비공개 피드백: </span>
                {customerReview.privateFeedback}
              </div>
            )}
            <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6 }}>
              {customerReview.createdAt?.toDate?.()?.toLocaleDateString('ko-KR') || ''}
            </div>
          </div>
        )}

        {/* 마케팅 수신 동의 */}
        <div className="card" style={{ marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 2 }}>마케팅 수신 동의</div>
              {enhanced.marketing_consent && enhanced.marketing_consent_at && (
                <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                  {formatDate(enhanced.marketing_consent_at)} 동의
                  {customerReview?.marketingConsent && <span style={{ marginLeft: 4, color: 'var(--green)' }}>· 리뷰에서 수집</span>}
                </div>
              )}
              {enhanced.opt_out && (
                <div style={{ fontSize: 11, color: 'var(--red)' }}>수신 거부</div>
              )}
            </div>
            <ToggleSwitch
              on={enhanced.marketing_consent && !enhanced.opt_out}
              disabled={enhanced.opt_out}
              onChange={() => toggleMarketingConsent(enhanced.id)}
            />
          </div>
        </div>

        {/* 액션 버튼 */}
        <div style={{ display: 'flex', gap: 8 }}>
          <button
            className="btn btn-outline"
            style={{ flex: 1, padding: '12px', fontSize: 13 }}
            onClick={() => { onSendMessage(enhanced); onClose(); }}
          >
            💬 메시지 발송
          </button>
          <button
            className="btn btn-green"
            style={{ flex: 1, padding: '12px', fontSize: 13, opacity: crmBlocked || crmCooldown ? 0.5 : 1 }}
            onClick={handleCrmSend}
            disabled={crmSending || crmBlocked || crmCooldown}
          >
            {crmSending ? <><div className="spinner" /> 발송 중...</> : (
              crmCooldown ? `📣 홍보 (${30 - lastDiscountDays}일 후)` : '📣 재방문 홍보 발송'
            )}
          </button>
        </div>

        {(crmBlocked || crmCooldown) && (
          <p style={{ fontSize: 11, color: 'var(--text-muted)', textAlign: 'center', marginTop: 8, marginBottom: 0 }}>
            {notCompleted
              ? '방문 완료 후 재방문 홍보가 가능합니다'
              : enhanced.opt_out
                ? '수신 거부 고객입니다'
                : crmCooldown
                  ? `${30 - lastDiscountDays}일 후 재발송 가능합니다`
                  : '마케팅 동의 후 CRM 발송 가능합니다'}
          </p>
        )}
      </div>
    </div>
  );
}

function InfoRow({ label, value, valueColor }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-light)' }}>
      <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 500, color: valueColor || 'var(--text-primary)' }}>{value}</span>
    </div>
  );
}

function ToggleSwitch({ on, onChange, disabled }) {
  return (
    <button
      onClick={onChange}
      disabled={disabled}
      style={{
        width: 44, height: 24, borderRadius: 12,
        background: on ? 'var(--green)' : 'var(--border)',
        border: 'none', cursor: disabled ? 'not-allowed' : 'pointer',
        position: 'relative', transition: 'background 0.2s', flexShrink: 0,
      }}
    >
      <div style={{
        width: 18, height: 18, borderRadius: '50%', background: 'white',
        position: 'absolute', top: 3, left: on ? 23 : 3,
        transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)',
      }} />
    </button>
  );
}

// ─── 직접 입력 파싱 ───
function parseDirectInput(text) {
  const lines = text.trim().split('\n').filter(l => l.trim());
  const results = [];
  const seen = new Set();
  for (const line of lines) {
    const phoneMatch = line.match(/0\d{1,2}[-\s]?\d{3,4}[-\s]?\d{4}/);
    const nameMatch = line.match(/[가-힣]{2,6}/);
    if (!phoneMatch || !nameMatch) continue;
    const rawPhone = phoneMatch[0].replace(/[-\s]/g, '');
    const phone = rawPhone.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
    if (seen.has(phone)) continue;
    seen.add(phone);
    results.push({ name: nameMatch[0], phone });
  }
  return results;
}

// ─── XLSX/CSV 파싱 ───
function parseSheetData(workbook) {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' });
  if (rows.length < 2) return [];

  const headers = rows[0].map(h => String(h).trim().toLowerCase());
  const NAME_KEYS = ['이름', '성명', '고객명', '예약자', '고객이름', 'name'];
  const PHONE_KEYS = ['전화번호', '연락처', '핸드폰', '휴대폰', '전화', 'phone', '휴대전화'];

  const nameIdx = headers.findIndex(h => NAME_KEYS.some(k => h.includes(k)));
  const phoneIdx = headers.findIndex(h => PHONE_KEYS.some(k => h.includes(k)));
  if (nameIdx === -1 || phoneIdx === -1) return null; // 컬럼 인식 실패

  const results = [];
  const seen = new Set();
  for (let i = 1; i < rows.length; i++) {
    const row = rows[i];
    const name = String(row[nameIdx] || '').trim();
    const rawPhone = String(row[phoneIdx] || '').replace(/[-\s]/g, '');
    if (!name || !rawPhone.match(/^0\d{9,10}$/)) continue;
    const phone = rawPhone.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
    if (seen.has(phone)) continue;
    seen.add(phone);
    results.push({ name, phone });
  }
  return results;
}

// ─── 기존 고객 불러오기 바텀 시트 ───
function ImportSheet({ onClose, onImported }) {
  const { importCustomers, showToast, state } = useApp();
  const isGuest = state.user?.isAnonymous;
  const [tab, setTab] = useState('text'); // 'text' | 'excel'

  // 직접 입력: 행 목록
  const [rows, setRows] = useState([{ name: '', phone: '' }]);

  const [preview, setPreview] = useState(null); // null | { entries, error }
  const [importing, setImporting] = useState(false);
  const fileRef = useRef(null);

  function updateRow(idx, field, value) {
    setRows(prev => prev.map((r, i) => i === idx ? { ...r, [field]: value } : r));
    setPreview(null);
  }

  function addRow() {
    setRows(prev => [...prev, { name: '', phone: '' }]);
  }

  function removeRow(idx) {
    setRows(prev => prev.length === 1 ? [{ name: '', phone: '' }] : prev.filter((_, i) => i !== idx));
    setPreview(null);
  }

  function handleConfirmRows() {
    const seen = new Set();
    const entries = [];
    const errors = [];
    rows.forEach((r, i) => {
      const name = r.name.trim();
      const rawPhone = r.phone.replace(/[-\s]/g, '');
      if (!name && !rawPhone) return; // 빈 줄 무시
      if (!name) { errors.push(`${i + 1}번 줄: 이름이 없습니다`); return; }
      if (!rawPhone.match(/^0\d{9,10}$/)) { errors.push(`${i + 1}번 줄(${name}): 올바른 전화번호가 아닙니다`); return; }
      const phone = rawPhone.replace(/^(\d{3})(\d{3,4})(\d{4})$/, '$1-$2-$3');
      if (seen.has(phone)) return;
      seen.add(phone);
      entries.push({ name, phone });
    });
    if (errors.length > 0) {
      setPreview({ entries: [], error: errors[0] });
    } else if (entries.length === 0) {
      setPreview({ entries: [], error: '입력된 고객이 없습니다.' });
    } else {
      setPreview({ entries, error: null });
    }
  }

  async function handleFileChange(e) {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const buf = await file.arrayBuffer();
      const wb = XLSX.read(buf, { type: 'array' });
      const entries = parseSheetData(wb);
      if (entries === null) {
        setPreview({ entries: [], error: '이름/전화번호 컬럼을 찾지 못했습니다. 컬럼명을 확인해주세요.' });
      } else if (entries.length === 0) {
        setPreview({ entries: [], error: '파일에서 유효한 고객 데이터를 찾지 못했습니다.' });
      } else {
        setPreview({ entries, error: null });
      }
    } catch {
      setPreview({ entries: [], error: '파일을 읽는 중 오류가 발생했습니다.' });
    }
    e.target.value = '';
  }

  async function handleImport() {
    if (!preview?.entries?.length) return;
    if (isGuest) { showToast('체험 계정에서는 고객 업로드를 사용할 수 없습니다.', 'error'); return; }
    setImporting(true);
    try {
      const imported = await importCustomers(preview.entries);
      onImported(imported);
      onClose();
      showToast(`기존 고객 ${imported.length}명이 등록됐습니다 ✓`);
    } catch {
      showToast('업로드 중 오류가 발생했습니다', 'error');
    } finally {
      setImporting(false);
    }
  }

  return (
    <>
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', bottom: 'var(--tabbar-h)', left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480, background: 'white',
        borderRadius: '22px 22px 0 0', zIndex: 301,
        maxHeight: '88dvh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -4px 30px rgba(0,0,0,0.18)',
      }}>
        {/* 핸들 */}
        <div style={{ padding: '12px 0 4px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ width: 38, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
        </div>

        {/* 헤더 */}
        <div style={{ padding: '4px 20px 12px', flexShrink: 0, borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>기존 고객 불러오기</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            불러온 고객에게 리뷰 요청 문자를 일괄 발송할 수 있습니다
          </div>
        </div>

        {/* 탭 */}
        <div style={{ display: 'flex', padding: '10px 20px 0', gap: 8, flexShrink: 0 }}>
          {[{ id: 'text', label: '✏️ 직접 입력' }, { id: 'excel', label: '📊 엑셀 업로드' }].map(t => (
            <button key={t.id} onClick={() => { setTab(t.id); setPreview(null); setRows([{ name: '', phone: '' }]); }}
              style={{
                flex: 1, padding: '9px', borderRadius: 10, fontSize: 13, fontWeight: 700, cursor: 'pointer',
                border: `2px solid ${tab === t.id ? 'var(--green)' : 'var(--border)'}`,
                background: tab === t.id ? 'var(--green-light)' : 'white',
                color: tab === t.id ? 'var(--green-dark)' : 'var(--text-secondary)',
              }}
            >{t.label}</button>
          ))}
        </div>

        {/* 본문 */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          {tab === 'text' ? (
            <>
              {/* 헤더 행 */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 28px', gap: 6, marginBottom: 6, padding: '0 2px' }}>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>이름</span>
                <span style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted)' }}>연락처</span>
                <span />
              </div>

              {/* 입력 행 목록 */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {rows.map((row, idx) => (
                  <div key={idx} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr 28px', gap: 6, alignItems: 'center' }}>
                    <input
                      className="input"
                      placeholder="홍길동"
                      value={row.name}
                      onChange={e => updateRow(idx, 'name', e.target.value)}
                      style={{ padding: '10px 10px', fontSize: 14 }}
                    />
                    <input
                      className="input"
                      placeholder="010-1234-5678"
                      type="tel"
                      value={row.phone}
                      onChange={e => updateRow(idx, 'phone', e.target.value)}
                      style={{ padding: '10px 10px', fontSize: 14 }}
                    />
                    <button
                      onClick={() => removeRow(idx)}
                      style={{
                        width: 28, height: 28, borderRadius: '50%', border: 'none',
                        background: '#f3f4f6', color: 'var(--text-muted)',
                        fontSize: 16, cursor: 'pointer', display: 'flex',
                        alignItems: 'center', justifyContent: 'center', flexShrink: 0,
                      }}
                    >×</button>
                  </div>
                ))}
              </div>

              {/* 행 추가 버튼 */}
              <button
                onClick={addRow}
                style={{
                  width: '100%', marginTop: 10, padding: '10px',
                  border: '1.5px dashed var(--border)', borderRadius: 10,
                  background: 'white', color: 'var(--text-muted)',
                  fontSize: 13, fontWeight: 600, cursor: 'pointer',
                }}
              >+ 고객 추가</button>

              <button
                onClick={handleConfirmRows}
                disabled={rows.every(r => !r.name.trim() && !r.phone.trim())}
                style={{
                  width: '100%', marginTop: 10, padding: '11px', borderRadius: 10,
                  border: 'none', fontSize: 14, fontWeight: 700, cursor: 'pointer',
                  background: rows.some(r => r.name.trim() || r.phone.trim()) ? 'var(--green)' : '#e5e7eb',
                  color: rows.some(r => r.name.trim() || r.phone.trim()) ? 'white' : 'var(--text-muted)',
                }}
              >고객 목록 확인</button>
            </>
          ) : (
            <>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 10 }}>
                <strong>이름</strong>과 <strong>전화번호</strong> 컬럼이 포함된 CSV 또는 XLSX 파일을 올려주세요
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)', background: '#f8f9fa', borderRadius: 8, padding: '8px 12px', marginBottom: 12, lineHeight: 1.7 }}>
                인식되는 컬럼명: 이름, 성명, 고객명, 예약자 / 전화번호, 연락처, 핸드폰
              </div>
              <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" style={{ display: 'none' }} onChange={handleFileChange} />
              <button
                onClick={() => fileRef.current?.click()}
                style={{
                  width: '100%', padding: '28px 16px',
                  border: '2px dashed var(--green-border)', borderRadius: 12,
                  background: 'var(--green-light)', cursor: 'pointer', textAlign: 'center',
                }}
              >
                <div style={{ fontSize: 32, marginBottom: 8 }}>📂</div>
                <div style={{ fontWeight: 700, fontSize: 14, color: 'var(--green-dark)' }}>파일 선택하기</div>
                <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>CSV · XLSX · XLS</div>
              </button>
            </>
          )}

          {/* 미리보기 */}
          {preview && (
            <div style={{ marginTop: 14 }}>
              {preview.error ? (
                <div style={{ padding: '12px', background: '#fef2f2', borderRadius: 10, color: '#dc2626', fontSize: 13, fontWeight: 600 }}>
                  ⚠ {preview.error}
                </div>
              ) : (
                <>
                  <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 8, color: 'var(--green-dark)' }}>
                    ✓ {preview.entries.length}명 인식됨
                  </div>
                  <div style={{ maxHeight: 180, overflowY: 'auto', border: '1px solid var(--border-light)', borderRadius: 10 }}>
                    {preview.entries.map((e, i) => (
                      <div key={i} style={{
                        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                        padding: '9px 14px', fontSize: 13,
                        borderBottom: i < preview.entries.length - 1 ? '1px solid var(--border-light)' : 'none',
                      }}>
                        <span style={{ fontWeight: 600 }}>{e.name}</span>
                        <span style={{ color: 'var(--text-muted)' }}>{e.phone}</span>
                      </div>
                    ))}
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* 하단 버튼 */}
        <div style={{ flexShrink: 0, padding: '12px 20px 16px', borderTop: '1px solid var(--border-light)', display: 'flex', gap: 8 }}>
          <button onClick={onClose} style={{
            flex: 1, padding: '13px', borderRadius: 12, border: '1.5px solid var(--border)',
            background: 'white', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>취소</button>
          <button
            onClick={handleImport}
            disabled={!preview?.entries?.length || importing}
            style={{
              flex: 2, padding: '13px', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 800, cursor: 'pointer',
              background: preview?.entries?.length && !importing
                ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : '#e5e7eb',
              color: preview?.entries?.length && !importing ? 'white' : 'var(--text-muted)',
              boxShadow: preview?.entries?.length && !importing ? '0 3px 14px rgba(34,197,94,0.35)' : 'none',
            }}
          >
            {importing ? '등록 중...' : preview?.entries?.length ? `${preview.entries.length}명 등록하기` : '등록하기'}
          </button>
        </div>
      </div>
    </>
  );
}

const DEFAULT_WELCOME_MSG = `안녕하세요 {이름}님 😊
예전에 저희 숙소 이용해주셨던 고객님 맞으시죠?
오랜만에 연락드립니다 🙏
다음 방문 시 혜택 안내 드리고 있습니다
받아보시려면 아래에서 간단히 선택해주세요 😊`;

// ─── 안부 메시지 일괄 발송 바텀 시트 ───
function WelcomeSendSheet({ customers, onClose, onDone }) {
  const { state, logMessage, showToast } = useApp();
  const { user } = state;
  const [msgText, setMsgText] = useState(DEFAULT_WELCOME_MSG);
  const [sending, setSending] = useState(false);
  const [done, setDone] = useState(0);

  async function handleSend() {
    if (user?.isAnonymous) { showToast('체험 계정에서는 발송할 수 없습니다.', 'error'); return; }
    setSending(true);
    let success = 0;
    for (const c of customers) {
      try {
        const token = await fsCreateWelcomeToken(user.uid, {
          customerId: c.id || null,
          customerName: c.name,
          customerPhone: c.phone,
          pensionName: user.pensionName || '',
        });
        const welcomeLink = `${WELCOME_BASE_URL}/${token}`;
        const personalMsg = msgText.replace(/\{이름\}/g, c.name) + `\n👉 ${welcomeLink}`;
        await sendMessage({ phone: c.phone, templateId: 'welcome', room: null, data: { name: c.name }, rawText: personalMsg });
        logMessage({ customerId: c.id, customerName: c.name, phone: c.phone, templateId: 'welcome', templateName: '안부 메시지', roomId: null, messageText: personalMsg });
        success++;
        setDone(success);
        await new Promise(r => setTimeout(r, 200));
      } catch {}
    }
    setSending(false);
    showToast(`안부 메시지 ${success}건 발송 완료!`);
    onDone();
  }

  return (
    <>
      <div onClick={!sending ? onClose : undefined} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 300 }} />
      <div style={{
        position: 'fixed', bottom: 'var(--tabbar-h)', left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: 480, background: 'white',
        borderRadius: '22px 22px 0 0', zIndex: 301,
        maxHeight: '88dvh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 -4px 30px rgba(0,0,0,0.18)',
      }}>
        <div style={{ padding: '12px 0 4px', display: 'flex', justifyContent: 'center', flexShrink: 0 }}>
          <div style={{ width: 38, height: 4, borderRadius: 2, background: '#e5e7eb' }} />
        </div>

        <div style={{ padding: '4px 20px 12px', flexShrink: 0, borderBottom: '1px solid var(--border-light)' }}>
          <div style={{ fontWeight: 800, fontSize: 16 }}>💌 안부 메시지 일괄 발송</div>
          <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 3 }}>
            발송 대상: {customers.length}명 · 메시지 수정 후 발송 가능
          </div>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '14px 20px' }}>
          <div style={{ background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 10, padding: '10px 14px', marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: '#92400e', fontWeight: 600, marginBottom: 2 }}>
              💡 최근 6개월 이내 방문 고객에게 발송을 권장합니다
            </div>
            <div style={{ fontSize: 11, color: '#b45309' }}>문자 비용은 건당 실비로 차감됩니다</div>
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>발송 대상</div>
          <div style={{ border: '1px solid var(--border-light)', borderRadius: 10, marginBottom: 14, maxHeight: 120, overflowY: 'auto' }}>
            {customers.map((c, i) => (
              <div key={c.phone} style={{
                display: 'flex', justifyContent: 'space-between', padding: '8px 14px', fontSize: 12,
                borderBottom: i < customers.length - 1 ? '1px solid var(--border-light)' : 'none',
              }}>
                <span style={{ fontWeight: 600 }}>{c.name}</span>
                <span style={{ color: 'var(--text-muted)' }}>{c.phone}</span>
              </div>
            ))}
          </div>

          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-secondary)', marginBottom: 6 }}>
            메시지 미리보기 <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>(수정 가능 · {'{이름}'}은 자동 치환)</span>
          </div>
          <textarea
            style={{
              width: '100%', minHeight: 160, padding: '12px', boxSizing: 'border-box',
              border: '1.5px solid var(--border)', borderRadius: 10,
              fontSize: 13, resize: 'vertical', fontFamily: 'inherit', lineHeight: 1.6,
            }}
            value={msgText}
            onChange={e => setMsgText(e.target.value)}
          />
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 6, lineHeight: 1.5 }}>
            * 발송 시 각 고객별 맞춤 링크(staymate.co.kr/welcome/…)가 자동으로 추가됩니다
          </div>
        </div>

        <div style={{ flexShrink: 0, padding: '12px 20px 16px', borderTop: '1px solid var(--border-light)', display: 'flex', gap: 8 }}>
          <button onClick={onClose} disabled={sending} style={{
            flex: 1, padding: '13px', borderRadius: 12, border: '1.5px solid var(--border)',
            background: 'white', color: 'var(--text-secondary)', fontSize: 14, fontWeight: 700, cursor: 'pointer',
          }}>취소</button>
          <button onClick={handleSend} disabled={sending || !msgText.trim()} style={{
            flex: 2, padding: '13px', borderRadius: 12, border: 'none', fontSize: 15, fontWeight: 800, cursor: 'pointer',
            background: !sending ? 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)' : '#e5e7eb',
            color: !sending ? 'white' : 'var(--text-muted)',
            boxShadow: !sending ? '0 3px 14px rgba(34,197,94,0.35)' : 'none',
          }}>
            {sending ? `발송 중... (${done}/${customers.length})` : `💌 ${customers.length}명에게 안부 메시지 발송`}
          </button>
        </div>
      </div>
    </>
  );
}

// ─── 메인 컴포넌트 ───
export default function CustomersTab({ onSendToCustomer }) {
  const { getCustomersWithStatus, getDaysSinceVisit, state } = useApp();
  const unreadReviews = (state.reviews || []).filter(r => !r.read);

  // 리뷰가 있는 고객 phone set (빠른 조회용)
  const reviewPhoneSet = useMemo(() => {
    const set = new Set();
    (state.reviews || []).forEach(r => { if (r.customerPhone) set.add(r.customerPhone); });
    return set;
  }, [state.reviews]);

  const unreadPhoneSet = useMemo(() => {
    const set = new Set();
    unreadReviews.forEach(r => { if (r.customerPhone) set.add(r.customerPhone); });
    return set;
  }, [unreadReviews]);

  const customers = getCustomersWithStatus();

  // 소스 필터 (전체 / 기존 고객 / 방문 고객)
  const [sourceFilter, setSourceFilter] = useState('all');
  // 상태 필터 (기본)
  const [statusFilter, setStatusFilter] = useState('all');
  // 보조 필터
  const [subFilter, setSubFilter] = useState('none');
  const [search, setSearch] = useState('');
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [importOpen, setImportOpen] = useState(false);
  const [welcomeTargets, setWelcomeTargets] = useState(null); // null | [{name, phone}]
  const [welcomeSendOpen, setWelcomeSendOpen] = useState(false);

  // 소스 필터 적용 기반 목록
  const sourceFiltered = useMemo(() => {
    if (sourceFilter === 'legacy') return customers.filter(c => c.badges?.includes('기존'));
    if (sourceFilter === 'visit')  return customers.filter(c => !c.badges?.includes('기존'));
    return customers;
  }, [customers, sourceFilter]);

  // 상태별 카운트 (소스 필터 반영)
  const counts = useMemo(() => ({
    all:      sourceFiltered.length,
    upcoming: sourceFiltered.filter(c => c.status === 'upcoming').length,
    staying:  sourceFiltered.filter(c => c.status === 'staying').length,
    completed: sourceFiltered.filter(c => c.status === 'completed' || c.status === 'none').length,
  }), [sourceFiltered]);

  // 보조 필터 카운트
  const subCounts = useMemo(() => ({
    revisit: sourceFiltered.filter(c => {
      if (!c.lastVisit || c.visits === 0) return false;
      return getDaysSinceVisit(c.lastVisit) >= 60;
    }).length,
    group:   sourceFiltered.filter(c => (c.guests || 0) >= 8 || c.badges?.includes('단체')).length,
    consent: sourceFiltered.filter(c => c.marketing_consent && !c.opt_out).length,
    reviews: sourceFiltered.filter(c => reviewPhoneSet.has(c.phone)).length,
  }), [sourceFiltered, getDaysSinceVisit, reviewPhoneSet]);

  // 필터 적용
  const filtered = useMemo(() => {
    let list = sourceFiltered;

    // 검색
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(c => c.name?.toLowerCase().includes(q) || c.phone?.includes(q));
    }

    // 상태 필터
    if (statusFilter === 'upcoming')  list = list.filter(c => c.status === 'upcoming');
    if (statusFilter === 'staying')   list = list.filter(c => c.status === 'staying');
    if (statusFilter === 'completed') list = list.filter(c => c.status === 'completed' || c.status === 'none');

    // 보조 필터
    if (subFilter === 'revisit') list = list.filter(c => {
      if (!c.lastVisit || c.visits === 0) return false;
      return getDaysSinceVisit(c.lastVisit) >= 60;
    });
    if (subFilter === 'group')   list = list.filter(c => (c.guests || 0) >= 8 || c.badges?.includes('단체'));
    if (subFilter === 'consent') list = list.filter(c => c.marketing_consent && !c.opt_out);
    if (subFilter === 'reviews') list = list.filter(c => reviewPhoneSet.has(c.phone));

    // 정렬: 투숙중 → 방문예정(가까운 순) → 방문완료(최근 순)
    return list.sort((a, b) => {
      const order = { staying: 0, upcoming: 1, completed: 2, none: 3 };
      const oa = order[a.status] ?? 3;
      const ob = order[b.status] ?? 3;
      if (oa !== ob) return oa - ob;
      if (a.status === 'upcoming') return (a.statusCheckin || '').localeCompare(b.statusCheckin || '');
      if (a.status === 'completed') return (b.lastVisit || '').localeCompare(a.lastVisit || '');
      return 0;
    });
  }, [customers, search, statusFilter, subFilter, getDaysSinceVisit]);

  const revisitCount = subCounts.revisit;

  return (
    <div className="tab-content">

      {/* ─── 새 리뷰 알림 배너 ─── */}
      {unreadReviews.length > 0 && (() => {
        const customers = getCustomersWithStatus();
        const matchedCount = customers.filter(c => unreadPhoneSet.has(c.phone)).length;
        if (matchedCount === 0) return null;
        return (
          <div
            className="card"
            style={{
              background: 'linear-gradient(135deg, #fffbeb, #fef3c7)',
              border: '1.5px solid #f59e0b', marginBottom: 12,
              display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer',
            }}
            onClick={() => setSubFilter('reviews')}
          >
            <span style={{ fontSize: 22 }}>⭐</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#78350f' }}>새 리뷰 {matchedCount}건</div>
              <div style={{ fontSize: 12, color: '#92400e' }}>탭하여 리뷰 고객 보기</div>
            </div>
          </div>
        );
      })()}

      {/* ─── 기존 고객 불러오기 버튼 ─── */}
      <button
        onClick={() => setImportOpen(true)}
        style={{
          width: '100%', marginBottom: 14, padding: '13px 16px',
          borderRadius: 12, border: 'none',
          background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 100%)',
          cursor: 'pointer', boxShadow: '0 2px 12px rgba(34,197,94,0.3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}
      >
        <span style={{ fontSize: 16 }}>📥</span>
        <span style={{ fontSize: 14, fontWeight: 700, color: 'white' }}>기존 고객 불러오기</span>
        <span style={{ fontSize: 12, color: 'rgba(255,255,255,0.8)', fontWeight: 500 }}>직접 입력 · 엑셀 업로드</span>
      </button>

      {/* ─── 안부 메시지 배너 (업로드 직후) ─── */}
      {welcomeTargets && welcomeTargets.length > 0 && (
        <div
          className="card"
          style={{
            background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
            border: '1.5px solid var(--green)',
            marginBottom: 12, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
          onClick={() => setWelcomeSendOpen(true)}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <span style={{ fontSize: 22 }}>💌</span>
            <div>
              <div style={{ fontWeight: 800, fontSize: 14, color: '#14532d' }}>
                기존 고객 {welcomeTargets.length}명에게 안부 메시지 보내기
              </div>
              <div style={{ fontSize: 12, color: '#16a34a', marginTop: 1 }}>탭하여 일괄 발송 →</div>
            </div>
          </div>
          <button
            onClick={e => { e.stopPropagation(); setWelcomeTargets(null); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 18, color: '#16a34a', padding: '4px 6px' }}
          >×</button>
        </div>
      )}

      {/* ─── 소스 필터 탭 (전체 / 기존 고객 / 방문 고객) ─── */}
      <div style={{
        display: 'flex', background: '#f3f4f6', borderRadius: 12,
        padding: 3, marginBottom: 12, gap: 2,
      }}>
        {[
          { id: 'all',    label: '전체', count: customers.length },
          { id: 'legacy', label: '기존 고객', count: customers.filter(c => c.badges?.includes('기존')).length },
          { id: 'visit',  label: '방문 고객', count: customers.filter(c => !c.badges?.includes('기존')).length },
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => { setSourceFilter(tab.id); setStatusFilter('all'); setSubFilter('none'); }}
            style={{
              flex: 1, padding: '8px 4px', borderRadius: 10, fontSize: 12, fontWeight: 700,
              border: 'none', cursor: 'pointer', transition: 'all 0.15s',
              background: sourceFilter === tab.id ? 'white' : 'transparent',
              color: sourceFilter === tab.id ? 'var(--text-primary)' : 'var(--text-muted)',
              boxShadow: sourceFilter === tab.id ? '0 1px 4px rgba(0,0,0,0.1)' : 'none',
            }}
          >
            {tab.label}
            <span style={{
              marginLeft: 4, fontSize: 11,
              color: sourceFilter === tab.id ? 'var(--green)' : 'var(--text-muted)',
              fontWeight: sourceFilter === tab.id ? 700 : 400,
            }}>
              {tab.count}
            </span>
          </button>
        ))}
      </div>

      {/* ─── 상태 필터 탭 ─── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4,1fr)', gap: 6, marginBottom: 10 }}>
        {[
          { id: 'all',       label: '전체',     value: counts.all,       color: 'var(--text-primary)' },
          { id: 'upcoming',  label: '방문 예정', value: counts.upcoming,  color: '#1d4ed8' },
          { id: 'staying',   label: '투숙 중',   value: counts.staying,   color: '#15803d' },
          { id: 'completed', label: '방문 완료', value: counts.completed, color: 'var(--text-secondary)' },
        ].map(tab => (
          <div
            key={tab.id}
            className="stat-card"
            style={{
              cursor: 'pointer',
              border: statusFilter === tab.id ? `2px solid ${tab.color}` : '1px solid var(--border)',
              padding: '10px 4px',
              background: statusFilter === tab.id ? (
                tab.id === 'upcoming' ? '#eff6ff' : tab.id === 'staying' ? '#f0fdf4' : 'white'
              ) : 'white',
            }}
            onClick={() => { setStatusFilter(tab.id); setSubFilter('none'); }}
          >
            <div className="stat-value" style={{ fontSize: 18, color: statusFilter === tab.id ? tab.color : 'var(--text-primary)' }}>
              {tab.value}
            </div>
            <div className="stat-label" style={{ fontSize: 10 }}>{tab.label}</div>
          </div>
        ))}
      </div>

      {/* ─── 보조 필터 칩 ─── */}
      <div style={{ display: 'flex', gap: 6, marginBottom: 12, overflowX: 'auto', paddingBottom: 2 }}>
        {[
          { id: 'none',    label: '전체' },
          { id: 'reviews', label: `⭐ 리뷰 ${subCounts.reviews}`, highlight: unreadReviews.length > 0 },
          { id: 'revisit', label: `재방문 가능 고객 ${subCounts.revisit}` },
          { id: 'group',   label: `단체 ${subCounts.group}` },
          { id: 'consent', label: `문자 가능 ${subCounts.consent}` },
        ].map(chip => (
          <button
            key={chip.id}
            onClick={() => setSubFilter(chip.id)}
            style={{
              flexShrink: 0, padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 600,
              border: `1.5px solid ${subFilter === chip.id ? 'var(--green)' : chip.highlight ? '#f59e0b' : 'var(--border)'}`,
              background: subFilter === chip.id ? 'var(--green-light)' : chip.highlight ? '#fffbeb' : 'white',
              color: subFilter === chip.id ? 'var(--green-dark)' : chip.highlight ? '#92400e' : 'var(--text-secondary)',
              cursor: 'pointer',
            }}
          >
            {chip.label}
          </button>
        ))}
      </div>

      {/* ─── 재방문 배너 ─── */}
      {statusFilter === 'all' && subFilter === 'none' && revisitCount > 0 && (
        <div
          className="card"
          style={{
            background: '#eff6ff', border: '1.5px solid #93c5fd',
            marginBottom: 12, cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          }}
          onClick={() => setSubFilter('revisit')}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <span style={{ fontSize: 18 }}>⏰</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 14, color: '#1e40af' }}>
                지금 다시 부르면 올 고객 {revisitCount}명 🔥
              </div>
              <div style={{ fontSize: 12, color: '#3b82f6' }}>최근 60일 방문이 없는 고객입니다</div>
            </div>
          </div>
          <span style={{ fontSize: 13, color: '#3b82f6', fontWeight: 600 }}>대상 보기 ›</span>
        </div>
      )}

      {/* ─── 검색 ─── */}
      <div style={{ marginBottom: 12, position: 'relative' }}>
        <input
          className="input"
          placeholder="고객 이름이나 번호로 찾기"
          value={search}
          onChange={e => setSearch(e.target.value)}
          style={{ paddingLeft: 36 }}
        />
        <span style={{
          position: 'absolute', left: 12, top: '50%',
          transform: 'translateY(-50%)', fontSize: 15, color: 'var(--text-muted)',
        }}>🔍</span>
        {search && (
          <button
            onClick={() => setSearch('')}
            style={{
              position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)',
              border: 'none', background: 'none', cursor: 'pointer', fontSize: 16, color: 'var(--text-muted)',
            }}
          >×</button>
        )}
      </div>

      {/* ─── 고객 리스트 ─── */}
      {filtered.length === 0 ? (
        <div className="empty-state">
          <div className="icon">👥</div>
          <p>{search ? '검색 결과가 없습니다' : '해당 고객이 없습니다'}</p>
          {!search && <p style={{ fontSize: 12 }}>발송 탭에서 메시지를 보내면 자동으로 등록됩니다</p>}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {filtered.map(customer => {
            const canCrm = customer.marketing_consent && !customer.opt_out;
            const daysSince = getDaysSinceVisit(customer.lastVisit);
            const needsRevisit = customer.status !== 'upcoming' && customer.status !== 'staying' && daysSince >= 60;
            const statusCfg = STATUS_CONFIG[customer.status] || STATUS_CONFIG.none;
            const hasUnread = unreadPhoneSet.has(customer.phone);
            const hasReview = reviewPhoneSet.has(customer.phone);

            return (
              <div
                key={customer.id}
                className="customer-item"
                style={{
                  cursor: 'pointer', position: 'relative',
                  borderLeft: `3px solid ${statusCfg.border}`,
                }}
                onClick={() => setSelectedCustomer(customer)}
              >
                {/* 재방문 알림 배지 (방문완료 고객만) */}
                {needsRevisit && (
                  <div style={{
                    position: 'absolute', top: 10, right: 10,
                    fontSize: 10, fontWeight: 700, padding: '2px 7px', borderRadius: 8,
                    background: daysSince >= 120 ? '#fef2f2' : '#fffbeb',
                    color: daysSince >= 120 ? '#dc2626' : '#b45309',
                    border: `1px solid ${daysSince >= 120 ? '#fca5a5' : '#fde68a'}`,
                  }}>
                    {daysSince >= 120 ? '놓치기 쉬운 고객' : '연락 추천'}
                  </div>
                )}

                {/* 아바타 */}
                <div className="customer-avatar" style={{
                  background: statusCfg.bg,
                  color: statusCfg.color,
                }}>
                  {customer.name?.[0] ?? '?'}
                </div>

                {/* 고객 정보 */}
                <div style={{ flex: 1, minWidth: 0 }}>
                  {/* 이름 + 상태 라벨 */}
                  <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 3, flexWrap: 'wrap' }}>
                    <span style={{ fontWeight: 700, fontSize: 15 }}>{customer.name}</span>
                    <StatusBadge status={customer.status} />
                    {hasUnread && (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#f59e0b', color: 'white', fontWeight: 700 }}>⭐ 새 리뷰</span>
                    )}
                    {!hasUnread && hasReview && (
                      <span style={{ fontSize: 10, padding: '2px 7px', borderRadius: 8, background: '#fef3c7', color: '#92400e', fontWeight: 700, border: '1px solid #fde68a' }}>⭐ 리뷰</span>
                    )}
                    {customer.badges?.includes('단골') && (
                      <span className="badge badge-green" style={{ fontSize: 10 }}>단골</span>
                    )}
                    {customer.badges?.includes('단체') && (
                      <span className="badge badge-gray" style={{ fontSize: 10 }}>단체</span>
                    )}
                    {customer.badges?.includes('기존') && (
                      <span style={{ fontSize: 10, padding: '2px 6px', borderRadius: 6, background: '#eff6ff', color: '#1d4ed8', border: '1px solid #bfdbfe', fontWeight: 700 }}>기존</span>
                    )}
                  </div>

                  {/* 전화·인원·채널 */}
                  <div style={{ fontSize: 12, color: 'var(--text-secondary)', display: 'flex', gap: 6, alignItems: 'center', marginBottom: 2 }}>
                    <span>{customer.phone} · {customer.guests}명</span>
                    {canCrm && <span style={{ fontSize: 10, color: 'var(--green)', fontWeight: 600 }}>문자가능</span>}
                    <ChannelBadge channel={customer.channel} />
                  </div>

                  {/* 상태별 날짜 문구 */}
                  <StatusDateLine customer={customer} />
                </div>

                {/* 화살표 */}
                <span style={{ color: 'var(--text-muted)', fontSize: 16, flexShrink: 0 }}>›</span>
              </div>
            );
          })}
        </div>
      )}

      {/* ─── 고객 상세 패널 ─── */}
      {selectedCustomer && (
        <CustomerDetail
          customer={selectedCustomer}
          onClose={() => setSelectedCustomer(null)}
          onSendMessage={onSendToCustomer}
        />
      )}

      {/* ─── 기존 고객 불러오기 시트 ─── */}
      {importOpen && (
        <ImportSheet
          onClose={() => setImportOpen(false)}
          onImported={(imported) => {
            setWelcomeTargets(imported);
            setImportOpen(false);
          }}
        />
      )}

      {/* ─── 안부 메시지 일괄 발송 시트 ─── */}
      {welcomeSendOpen && welcomeTargets && (
        <WelcomeSendSheet
          customers={welcomeTargets}
          onClose={() => setWelcomeSendOpen(false)}
          onDone={() => { setWelcomeSendOpen(false); setWelcomeTargets(null); }}
        />
      )}
    </div>
  );
}
